import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { projectsApi, settingsApi } from '../services/api';
import { useEntitlements } from './useEntitlements';
import { isNative } from '../native/platform';
import i18n from '../i18n';

const FREE_VIEWS = new Set(['list', 'table']);

// The main view of a scope: on every plan, always first, never deleted or hidden.
// Home is the day's agenda.
const PINNED_TYPE = { home: 'agenda' };

// Module-level settings cache - shared across all useViewTabs callers.
// Settings change rarely; refetching on every project switch produces a
// visible ~full-roundtrip delay for each navigation. TTL is short enough
// that manual out-of-band mutations (e.g. another tab) catch up quickly.
const SETTINGS_TTL_MS = 60 * 1000;
let _settingsPromise = null;
let _settingsFetchedAt = 0;
function fetchSettings() {
  const now = Date.now();
  if (!_settingsPromise || now - _settingsFetchedAt > SETTINGS_TTL_MS) {
    _settingsFetchedAt = now;
    _settingsPromise = settingsApi.get().catch(() => ({ data: null }));
  }
  return _settingsPromise;
}
function invalidateSettingsCache() {
  _settingsPromise = null;
  _settingsFetchedAt = 0;
}

// The last tabs each scope showed, so it draws them at once while they are loaded
// again (no skeleton on every visit). The phone app keeps them on the device too, so
// it opens straight on its tabs (src/lib/persistCache.js does the same for queries).
const REMEMBER_KEY = 'bb.viewtabs';
const remembered = new Map();
try {
  if (isNative()) Object.entries(JSON.parse(localStorage.getItem(REMEMBER_KEY) || '{}')).forEach(([k, v]) => remembered.set(k, v));
} catch { /* nothing kept */ }
function remember(key, value) {
  remembered.delete(key);
  remembered.set(key, value);
  while (remembered.size > 60) remembered.delete(remembered.keys().next().value);
  if (!isNative()) return;
  try { localStorage.setItem(REMEMBER_KEY, JSON.stringify(Object.fromEntries(remembered))); } catch { /* full */ }
}

function prefsPathFor(scope) {
  if (scope.kind === 'section') return `ui_preferences.section_view_prefs.${scope.sectionId}`;
  if (scope.kind === 'project') return `ui_preferences.project_view_prefs.${scope.projectId}`;
  if (scope.kind === 'home') return 'ui_preferences.home_view_prefs';
  return 'ui_preferences.tasks_page_view_prefs';
}

// UIPreferences field holding the catalog for per-user scopes.
function catalogField(scope) {
  if (scope.kind === 'home') return 'home_views';
  return 'tasks_page_views';
}

// Scope driver: uniform interface over the shared project catalog (one per
// project) and the per-user catalogs living inside UIPreferences.
function useDriver(scope) {
  // The driver depends on these three only (callers pass a new scope object every render).
  const { kind, projectId, sectionId } = scope;
  return useMemo(() => {
    if (kind === 'section') {
      const sid = sectionId;
      return {
        async loadCatalog() {
          const res = await projectsApi.getSectionViews(sid);
          return res.data || [];
        },
        async addView(payload) {
          const res = await projectsApi.addSectionView(sid, payload);
          return res.data;
        },
        async updateView(id, payload) {
          const res = await projectsApi.updateSectionView(sid, id, payload);
          return res.data;
        },
        async deleteView(id) {
          await projectsApi.deleteSectionView(sid, id);
        },
      };
    }
    if (kind === 'project') {
      const pid = projectId;
      return {
        async loadCatalog() {
          const res = await projectsApi.getViews(pid);
          return res.data || [];
        },
        async addView(payload) {
          const res = await projectsApi.addView(pid, payload);
          return res.data;
        },
        async updateView(id, payload) {
          const res = await projectsApi.updateView(pid, id, payload);
          return res.data;
        },
        async deleteView(id) {
          await projectsApi.deleteView(pid, id);
        },
      };
    }
    // per-user scope (tasks / home) - catalog lives in settings.
    const field = catalogField({ kind });
    const fieldPath = `ui_preferences.${field}`;
    return {
      async loadCatalog() {
        const res = await fetchSettings();
        return res.data?.ui_preferences?.[field] || [];
      },
      async addView(payload) {
        const cur = await this.loadCatalog();
        const sameType = cur.filter(v => v.type === payload.type).length;
        const name = payload.name
          || (sameType ? `${cap(payload.type)} ${sameType + 1}` : cap(payload.type));
        const view = {
          id: cryptoId(),
          name,
          type: payload.type,
          config: payload.config || {},
          created_at: new Date().toISOString(),
        };
        invalidateSettingsCache();
        await settingsApi.update({ [fieldPath]: [...cur, view] });
        return view;
      },
      async updateView(id, payload) {
        const cur = await this.loadCatalog();
        const next = cur.map(v => v.id === id ? { ...v, ...payload } : v);
        invalidateSettingsCache();
        await settingsApi.update({ [fieldPath]: next });
        return next.find(v => v.id === id);
      },
      async deleteView(id) {
        const cur = await this.loadCatalog();
        invalidateSettingsCache();
        await settingsApi.update({ [fieldPath]: cur.filter(v => v.id !== id) });
      },
    };
  }, [kind, projectId, sectionId]);
}

function cap(s) { return s && s[0].toUpperCase() + s.slice(1); }
function cryptoId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// Deterministic ordering: preserve user's explicit order, append catalog
// entries we've not seen yet (e.g. a teammate added a new shared view).
// The pinned view (see PINNED_TYPE) always comes first and cannot be hidden.
function pinnedIdOf(catalog, pinnedType) {
  return pinnedType ? catalog.find(v => v.type === pinnedType)?.id || null : null;
}

function resolveTabs(catalog, prefs, isLocked, pinnedType = null) {
  const byId = new Map(catalog.map(v => [v.id, v]));
  const pinnedId = pinnedIdOf(catalog, pinnedType);
  const hidden = new Set((prefs.hidden || []).filter(id => id !== pinnedId));
  const visible = catalog.filter(v => !hidden.has(v.id));
  const visibleIds = new Set(visible.map(v => v.id));

  const known = (prefs.order || []).filter(id => visibleIds.has(id));
  const unknown = visible.filter(v => !prefs.order?.includes(v.id)).map(v => v.id);
  const orderedIds = [...known, ...unknown].filter(id => id !== pinnedId);
  if (pinnedId) orderedIds.unshift(pinnedId);

  return orderedIds.map(id => {
    const v = byId.get(id);
    return {
      ...v,
      isDefault: prefs.default_view_id === id,
      locked: isLocked(v.type),
      pinned: id === pinnedId,
    };
  });
}

function resolveActiveId(tabs, prefs) {
  const firstUnlocked = tabs.find(t => !t.locked);
  const fallback = firstUnlocked?.id || tabs[0]?.id || null;
  const pickable = (id) => {
    const t = tabs.find(x => x.id === id);
    return t && !t.locked ? id : null;
  };
  // A pinned main view is where the page opens, unless the person chose another default.
  return pickable(prefs.default_view_id)
    || pickable(tabs.find(t => t.pinned)?.id)
    || pickable(prefs.last_active_view_id)
    || fallback;
}

// The view tabs of a read-only list (the public team page): one view, nothing loaded or saved.
// A plain function, not a hook: SectionBlock picks it or useViewTabs by component, so no
// component ever calls hooks conditionally.
export function readOnlyViewTabs(seedViewType) {
  const view = {
    id: 'public-seed',
    name: (seedViewType && seedViewType[0].toUpperCase() + seedViewType.slice(1)) || 'List',
    type: seedViewType || 'list',
    config: {},
    isDefault: true,
    locked: false,
  };
  const noop = () => {};
  return {
    loading: false,
    tabs: [view],
    activeView: view,
    hiddenCatalog: [],
    catalog: [view],
    isLocked: () => false,
    actions: {
      setActive: noop, addView: noop, renameView: noop, updateView: noop,
      removeView: noop, reorder: noop, setDefault: noop, hide: noop, unhide: noop,
    },
  };
}

export function useViewTabs(scope) {
  const { has } = useEntitlements();
  const allViewsEnabled = has('all_views');
  const pinnedType = PINNED_TYPE[scope.kind] || null;
  const isLocked = useCallback(
    (type) => !allViewsEnabled && !FREE_VIEWS.has(type) && type !== pinnedType,
    [allViewsEnabled, pinnedType],
  );

  const driver = useDriver(scope);
  const EMPTY_PREFS = { order: [], hidden: [], default_view_id: null, last_active_view_id: null };
  const prefsPath = prefsPathFor(scope);
  const scopeKey = scope.kind === 'section'
    ? `section:${scope.sectionId}`
    : scope.kind === 'project'
      ? `project:${scope.projectId}`
      : scope.kind === 'home' ? 'home' : 'tasks';
  const seen = remembered.get(scopeKey);
  const [catalog, setCatalog] = useState(() => seen?.catalog || []);
  const [prefs, setPrefs] = useState(() => seen?.prefs || EMPTY_PREFS);
  const [activeId, setActiveIdState] = useState(() => seen?.activeId || null);
  const [loading, setLoading] = useState(() => !seen);

  // Derived state reset: when the scope changes, clear catalog/prefs/active
  // synchronously in the same render that detected the change - otherwise
  // React would render once with the previous project's tabs still visible
  // before the async load completes. (Pattern from React docs on "Storing
  // information from previous renders".)
  const [trackedScope, setTrackedScope] = useState(scopeKey);
  const loadGenerationRef = useRef(0);
  if (trackedScope !== scopeKey) {
    setTrackedScope(scopeKey);
    setCatalog(seen?.catalog || []);
    setPrefs(seen?.prefs || EMPTY_PREFS);
    setActiveIdState(seen?.activeId || null);
    setLoading(!seen);
    // Bump generation - any in-flight load for the previous scope
    // will see a mismatch and discard its result.
    loadGenerationRef.current += 1;
  }

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;  // again when a kept tab is shown (layouts/TabScreens.jsx)
    return () => { mountedRef.current = false; };
  }, []);

  // Load catalog + prefs together. Settings come from a module-level cache
  // so repeated project switches don't hit the network each time. Once per scope:
  // a kept tab shown again runs this again and would draw the page twice for nothing.
  const loadedScopeRef = useRef(null);
  useEffect(() => {
    if (scope.kind === 'project' && !scope.projectId) return;
    if (scope.kind === 'section' && !scope.sectionId) return;
    if (loadedScopeRef.current === scopeKey) return;
    const gen = ++loadGenerationRef.current;
    setLoading(!remembered.has(scopeKey));
    let cancelled = false;
    (async () => {
      try {
        const remoteCatalogScopes = scope.kind === 'project' || scope.kind === 'section';
        const [cat, settingsRes] = await Promise.all([
          remoteCatalogScopes ? driver.loadCatalog() : Promise.resolve(null),
          fetchSettings(),
        ]);
        if (cancelled || gen !== loadGenerationRef.current) return;
        const settings = settingsRes.data || {};
        const field = remoteCatalogScopes ? null : catalogField(scope);
        const cat2 = remoteCatalogScopes
          ? cat
          : (settings.ui_preferences?.[field] || []);
        // Per-user scopes seed their catalog on first use so the user always
        // has something to switch between. Seeds match the page's purpose:
        // Agenda first on home, List+Table on My Tasks.
        let seededCat = cat2;
        if (!remoteCatalogScopes && cat2.length > 0 && pinnedType && !cat2.some(v => v.type === pinnedType)) {
          // The main view was deleted before it was pinned: bring it back, first.
          const now = new Date().toISOString();
          seededCat = [{ id: cryptoId(), name: cap(pinnedType), type: pinnedType, config: {}, created_at: now }, ...cat2];
          invalidateSettingsCache();
          await settingsApi.update({ [`ui_preferences.${field}`]: seededCat });
        }
        if (!remoteCatalogScopes && cat2.length === 0) {
          const now = new Date().toISOString();
          // Table is the primary default everywhere (tasks/projects/sections)
          // except Home, whose main view is the day's agenda.
          const seed = scope.kind === 'home'
            ? [
                { id: cryptoId(), name: 'Agenda', type: 'agenda', config: {}, created_at: now },
                { id: cryptoId(), name: 'Table', type: 'table', config: {}, created_at: now },
              ]
            : [
                { id: cryptoId(), name: 'Table', type: 'table', config: {}, created_at: now },
                { id: cryptoId(), name: 'List', type: 'list', config: {}, created_at: now },
              ];
          invalidateSettingsCache();
          await settingsApi.update({ [`ui_preferences.${field}`]: seed });
          seededCat = seed;
        }
        const storedPrefs = scope.kind === 'section'
          ? (settings.ui_preferences?.section_view_prefs?.[scope.sectionId] || {})
          : scope.kind === 'project'
            ? (settings.ui_preferences?.project_view_prefs?.[scope.projectId] || {})
            : scope.kind === 'home'
              ? (settings.ui_preferences?.home_view_prefs || {})
              : (settings.ui_preferences?.tasks_page_view_prefs || {});
        const nextPrefs = {
          order: storedPrefs.order || [],
          hidden: storedPrefs.hidden || [],
          default_view_id: storedPrefs.default_view_id || null,
          last_active_view_id: storedPrefs.last_active_view_id || null,
        };
        setCatalog(seededCat);
        setPrefs(nextPrefs);
        setActiveIdState(resolveActiveId(
          resolveTabs(seededCat, nextPrefs, isLocked, pinnedType),
          nextPrefs,
        ));
        loadedScopeRef.current = scopeKey;
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [scopeKey]);

  // What the scope shows now is what it draws first next time.
  useEffect(() => {
    if (!loading && trackedScope === scopeKey && catalog.length) remember(scopeKey, { catalog, prefs, activeId });
  }, [loading, trackedScope, scopeKey, catalog, prefs, activeId]);

  const tabs = useMemo(() => resolveTabs(catalog, prefs, isLocked, pinnedType), [catalog, prefs, isLocked, pinnedType]);
  const pinnedId = pinnedIdOf(catalog, pinnedType);
  const activeView = tabs.find(t => t.id === activeId) || null;

  // ---- Debounced prefs save ----
  const saveTimerRef = useRef(null);
  const pendingPrefsRef = useRef(null);
  const pendingPathRef = useRef(prefsPath);
  pendingPathRef.current = prefsPath;

  const persistPrefs = useCallback((nextPrefs) => {
    setPrefs(nextPrefs);
    pendingPrefsRef.current = nextPrefs;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      const pending = pendingPrefsRef.current;
      pendingPrefsRef.current = null;
      saveTimerRef.current = null;
      invalidateSettingsCache();
      settingsApi.update({ [pendingPathRef.current]: pending }).catch(() => {});
    }, 300);
  }, []);

  // Flush pending save on unmount so we never lose a reorder.
  useEffect(() => () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      const pending = pendingPrefsRef.current;
      if (pending) {
        invalidateSettingsCache();
        settingsApi.update({ [pendingPathRef.current]: pending }).catch(() => {});
      }
    }
  }, []);

  // ---- Actions ----
  const setActive = useCallback((id) => {
    const t = tabs.find(x => x.id === id);
    if (!t || t.locked) return;
    setActiveIdState(id);
    persistPrefs({ ...prefs, last_active_view_id: id });
  }, [tabs, prefs, persistPrefs]);

  const addView = useCallback(async (type, options = {}) => {
    if (isLocked(type)) {
      window.dispatchEvent(new CustomEvent('upgradNeeded', {
        detail: { feature: 'all_views', message: i18n.t('ui_views.tabs.requires_basic') },
      }));
      return null;
    }
    try {
      const created = await driver.addView({ type, name: options.name });
      setCatalog(prev => [...prev, created]);
      // Persist a complete order map so the new view lands on the right end.
      //   - Without this, a user whose prefs.order was still empty would end
      //     up with order=[new_id]; resolveTabs would then treat every
      //     pre-existing catalog id as "unknown" and shove them AFTER the new
      //     view, flipping it to the leftmost slot.
      //   - We preserve any existing slots (incl. hidden ones, so unhide later
      //     restores position), append catalog ids not yet in order in catalog
      //     order, then put the new id last.
      const existingOrder = prefs.order || [];
      const catalogIdsBefore = catalog.map(v => v.id);
      const nextOrder = [
        ...existingOrder.filter(id => catalogIdsBefore.includes(id) && id !== created.id),
        ...catalogIdsBefore.filter(id => !existingOrder.includes(id)),
        created.id,
      ];
      persistPrefs({ ...prefs, order: nextOrder, last_active_view_id: created.id });
      setActiveIdState(created.id);
      return created;
    } catch (err) {
      if (err?.response?.status === 402) {
        window.dispatchEvent(new CustomEvent('upgradNeeded', {
          detail: { feature: 'all_views', message: i18n.t('ui_views.tabs.requires_higher_plan') },
        }));
      }
      return null;
    }
  }, [driver, isLocked, prefs, persistPrefs, catalog]);

  const renameView = useCallback(async (id, name) => {
    const prev = catalog.find(v => v.id === id);
    if (!prev || prev.name === name) return;
    setCatalog(c => c.map(v => v.id === id ? { ...v, name } : v));
    try {
      await driver.updateView(id, { name });
    } catch {
      setCatalog(c => c.map(v => v.id === id ? { ...v, name: prev.name } : v));
    }
  }, [catalog, driver]);

  // Generic patch - used for config updates (e.g. visible_fields). Updates
  // the catalog optimistically and rolls back on failure. Uses a functional
  // setCatalog so rapid successive toggles can't lose updates to a stale
  // closure on `catalog`.
  const updateView = useCallback(async (id, patch) => {
    let prev = null;
    setCatalog(c => {
      const target = c.find(v => v.id === id);
      if (!target) return c;
      prev = target;
      return c.map(v => v.id === id ? { ...v, ...patch } : v);
    });
    if (!prev) return;
    try {
      await driver.updateView(id, patch);
    } catch {
      const original = prev;
      setCatalog(c => c.map(v => v.id === id ? original : v));
    }
  }, [driver]);

  const removeView = useCallback(async (id) => {
    if (catalog.length <= 1 || id === pinnedId) return;
    const prevCatalog = catalog;
    setCatalog(c => c.filter(v => v.id !== id));
    const nextPrefs = {
      ...prefs,
      order: (prefs.order || []).filter(x => x !== id),
      hidden: (prefs.hidden || []).filter(x => x !== id),
      default_view_id: prefs.default_view_id === id ? null : prefs.default_view_id,
      last_active_view_id: prefs.last_active_view_id === id ? null : prefs.last_active_view_id,
    };
    persistPrefs(nextPrefs);
    if (activeId === id) {
      const fallback = tabs.find(t => t.id !== id && !t.locked);
      if (fallback) setActiveIdState(fallback.id);
    }
    try {
      await driver.deleteView(id);
    } catch (err) {
      setCatalog(prevCatalog);
      if (err?.response?.status === 400) return;
    }
  }, [catalog, driver, prefs, persistPrefs, activeId, tabs, pinnedId]);

  const reorder = useCallback((orderedIds) => {
    persistPrefs({ ...prefs, order: orderedIds });
  }, [prefs, persistPrefs]);

  const setDefault = useCallback((id) => {
    const t = tabs.find(x => x.id === id);
    if (!t || t.locked) return;
    persistPrefs({ ...prefs, default_view_id: id });
  }, [tabs, prefs, persistPrefs]);

  const hide = useCallback((id) => {
    // Don't allow hiding the last visible tab, nor the pinned main view.
    if (tabs.length <= 1 || id === pinnedId) return;
    const nextHidden = Array.from(new Set([...(prefs.hidden || []), id]));
    // BG_90: keep `default_view_id` intact when hiding the default view.
    // Hiding is a visibility toggle, not a default reset - the user expects
    // the default to be restored when the view is unhidden. While hidden the
    // view is filtered out of `tabs`, so resolveActiveId's pickable() skips it
    // and falls back to last_active/first-unlocked (no misbehaviour). Only
    // removeView (a permanent delete) should clear the default.
    const nextPrefs = {
      ...prefs,
      hidden: nextHidden,
    };
    persistPrefs(nextPrefs);
    if (activeId === id) {
      const fallback = tabs.find(t2 => t2.id !== id && !t2.locked);
      if (fallback) setActiveIdState(fallback.id);
    }
  }, [tabs, prefs, persistPrefs, activeId, pinnedId]);

  const unhide = useCallback((id) => {
    persistPrefs({ ...prefs, hidden: (prefs.hidden || []).filter(x => x !== id) });
  }, [prefs, persistPrefs]);

  const hiddenCatalog = useMemo(
    () => catalog.filter(v => (prefs.hidden || []).includes(v.id)),
    [catalog, prefs.hidden],
  );

  return {
    loading,
    tabs,
    activeView,
    hiddenCatalog,
    catalog,
    isLocked,
    actions: { setActive, addView, renameView, updateView, removeView, reorder, setDefault, hide, unhide },
  };
}
